/**
 * Notification Panel
 *
 * Dropdown panel showing the notification list with infinite scroll,
 * loading/empty states, and mark-all-read action.
 */

import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NotificationItem } from "./NotificationItem";
import { useNotificationStore } from "@/stores/useNotificationStore";
import {
  useNotificationList,
  useNotificationMutations,
} from "@/hooks/useNotifications";
import { resolveChatNotificationTitle } from "@/utils/chatNotificationLabel";
import { cn } from "@/lib/utils";

/** Server notification type for a durable chat-message bell row. */
const CHAT_MESSAGE_TYPE = "chat_message";

interface NotificationPanelProps {
  onOpenPreferences?: () => void;
}

export function NotificationPanel({
  onOpenPreferences,
}: NotificationPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isPanelOpen = useNotificationStore((s) => s.isPanelOpen);
  const closePanel = useNotificationStore((s) => s.closePanel);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const serverNotifications = useNotificationStore((s) => s.notifications);

  const { isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useNotificationList();
  const { markAsRead, markAllAsRead, deleteNotification, isMarkingAllRead } =
    useNotificationMutations();

  // Durable chat-message notifications carry a generic "New message" title. Swap
  // in a friendlier label resolved from the chats-list cache (branded
  // "Studio1 Support" for tickets; the channel display name for a DM). Reading
  // the cache never triggers a fetch, so this is safe on every render.
  const notifications = useMemo(
    () =>
      serverNotifications.map((n) => {
        if (n.type !== CHAT_MESSAGE_TYPE) return n;
        const meta = n.metadata ?? {};
        const channelId =
          typeof meta.channelId === "string" ? meta.channelId : undefined;
        const ticketId =
          typeof meta.ticketId === "string" ? meta.ticketId : undefined;
        const title = resolveChatNotificationTitle({
          queryClient,
          channelId,
          ticketId,
        });
        return title === n.title ? n : { ...n, title };
      }),
    [serverNotifications, queryClient],
  );

  const handleMarkRead = (id: string) => {
    markAsRead(id);
  };

  const handleDelete = (id: string) => {
    deleteNotification(id);
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const panelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Close on click outside is handled by the backdrop overlay below

  // Close on Escape
  useEffect(() => {
    if (!isPanelOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closePanel();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isPanelOpen, closePanel]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!isPanelOpen) return null;

  const handleNavigate = (url: string) => {
    closePanel();
    navigate(url);
  };

  return (
    <>
      {/* Invisible backdrop to close panel on outside click */}
      <div
        className="fixed inset-0 z-40"
        onClick={closePanel}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        data-testid="notification-panel"
        className={cn(
          "absolute top-full right-0 mt-2",
          "w-[420px] max-h-[70vh]",
          "flex flex-col overflow-hidden",
          "z-50",
          "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
          "duration-200",
        )}
        style={{
          backgroundColor: "var(--color-panel)",
          border: "1px solid hsl(var(--border))",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
        }}
        role="dialog"
        aria-label="Notifications"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: "1px solid hsl(var(--border))" }}
        >
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-semibold text-sm">Notifications</h2>
            {unreadCount > 0 && (
              <span
                data-testid="notification-panel-unread-count"
                className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full"
              >
                {unreadCount} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-0.5">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="notification-mark-all-read-button"
                onClick={handleMarkAllRead}
                disabled={isMarkingAllRead}
                className="text-xs h-7 px-2 text-muted-foreground hover:text-foreground hover:bg-accent/30"
                style={{ borderRadius: "6px" }}
              >
                <CheckCheck className="h-3.5 w-3.5 mr-1" />
                Mark all read
              </Button>
            )}
            {onOpenPreferences && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-accent/30"
                style={{ borderRadius: "8px" }}
                onClick={onOpenPreferences}
                aria-label="Notification settings"
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Notification List */}
        <ScrollArea className="flex-1 max-h-[calc(70vh-64px)]">
          <div role="list" aria-label="Notification list">
            {isLoading && notifications.length === 0 ? (
              // Loading skeleton placeholders
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="flex gap-3 py-3 px-4 border-b border-border/50 animate-pulse"
                >
                  <div className="h-8 w-8 rounded-full bg-muted" />
                  <div className="flex-1 space-y-2 pt-1">
                    <div className="h-3.5 w-2/5 rounded-md bg-muted" />
                    <div className="h-3 w-4/5 rounded-md bg-muted" />
                    <div className="h-2.5 w-1/4 rounded-md bg-muted" />
                  </div>
                </div>
              ))
            ) : notifications.length === 0 ? (
              // Empty state
              <div
                data-testid="notification-list-empty"
                className="flex flex-col items-center justify-center py-12 px-8 text-center"
              >
                <div className="h-14 w-14 rounded-full bg-muted/60 flex items-center justify-center mb-4">
                  <Bell className="h-7 w-7 text-muted-foreground/40" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  All caught up
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  No new notifications to show
                </p>
              </div>
            ) : (
              <div data-testid="notification-list">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={handleMarkRead}
                    onDelete={handleDelete}
                    onNavigate={handleNavigate}
                  />
                ))}

                {/* Infinite scroll trigger */}
                <div ref={loadMoreRef} className="h-4" />

                {isFetchingNextPage && (
                  <div className="p-4 text-center">
                    <div className="h-4 w-24 mx-auto rounded bg-muted animate-pulse" />
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </>
  );
}
